// S-3050A: Unified Video Analysis page supporting both Cloudflare (swing_runs) and legacy (videos)
// MVPFIGMA: Updated to match AnalysisSummary design
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Loader2, Target, TrendingUp, Play, AlertCircle, ChevronDown, ChevronUp, Info, ListChecks, FileText } from 'lucide-react';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from "@/components/golf/golf-card";
import { GolfButton } from "@/components/golf/golf-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { VideoPlayer } from "@/features/swing-lab/components/VideoPlayer";
import OverlayCanvas from "@/features/swing-lab/analysis/OverlayCanvas";
import { OverlayController } from "@/features/telemetry2/OverlayController";
import type { PoseData } from "@/features/telemetry2/types";
import type { SwingRun } from '@/types/swing-run';
import type { Database } from '@/integrations/supabase/types';
import { FEATURE_FLAGS } from '@/lib/feature-flags';
import Hls from 'hls.js';
import LogoOption4 from "@/components/logo-variations/LogoOption4";
import { BottomTabs } from "@/mvp/navigation/BottomTabs";
import { ScoreInfoIcon } from "@/components/scoring/ScoreInfoIcon";
import { ProcessingNarrative } from "@/components/processing/ProcessingNarrative";

type VideoData = Database['public']['Tables']['videos']['Row'];

interface PoseFrame {
  landmarks: Array<{
    x: number;
    y: number;
    z: number;
    visibility: number;
  }>;
  timestamp: number;
  frameIndex: number;
}

/**
 * Flaw card component with expandable reasoning
 */
function FlawCardWithReasoning({ flaw }: { flaw: any }) {
  const [showReasoning, setShowReasoning] = useState(false);
  
  // Debug logging - more explicit
  React.useEffect(() => {
    const hasReasoning = !!flaw.reasoning;
    const reasoningType = typeof flaw.reasoning;
    const reasoningValue = flaw.reasoning;
    
    console.log('[FlawCardWithReasoning] Rendering flaw:', {
      flaw_id: flaw.flaw_id,
      display_name: flaw.display_name,
      hasReasoning,
      reasoningType,
      reasoningValue: reasoningValue ? reasoningValue.substring(0, 100) : 'null/undefined',
      reasoningLength: flaw.reasoning?.length || 0,
      allFlawKeys: Object.keys(flaw)
    });
    
    // Explicit check - log if reasoning exists but button won't show
    if (hasReasoning) {
      console.log('[FlawCardWithReasoning] ✅ Reasoning found - button should appear');
    } else {
      console.warn('[FlawCardWithReasoning] ❌ No reasoning found - button will NOT appear');
    }
  }, [flaw]);
  
  // Explicit check for reasoning - handle null, undefined, empty string
  const hasReasoning = flaw.reasoning && typeof flaw.reasoning === 'string' && flaw.reasoning.trim().length > 0;
  
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-gray-50">
      <Target className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-semibold text-[#0D382A] mb-1">
          {flaw.display_name || flaw.name}
        </p>
        <p className="text-sm text-gray-600 mb-2">
          {flaw.short_description || flaw.player_language_symptoms}
        </p>
        {hasReasoning ? (
          <div className="mt-2">
            <button
              onClick={() => setShowReasoning(!showReasoning)}
              className="text-xs text-[#196644] hover:text-[#0D382A] font-medium flex items-center gap-1"
            >
              {showReasoning ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Hide Technical Analysis
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  Show Technical Analysis
                </>
              )}
            </button>
            {showReasoning && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-medium text-blue-900 mb-1">How we detected this:</p>
                <p className="text-xs text-blue-800 leading-relaxed whitespace-pre-wrap">
                  {flaw.reasoning}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-2 text-xs text-gray-400 italic">
            {/* Debug: Show if reasoning is missing */}
            {import.meta.env.DEV && (
              <span>No reasoning available (debug: {typeof flaw.reasoning})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Map coaching_result (coaching_v2 schema) to UI format
 * Falls back to legacy ai_result (coaching_v1) if coaching_result not available
 * Also includes reasoning from analysis_data if available
 */
function mapCoachingToUI(coaching_result: any, ai_result?: any, analysis_data?: any): any {
  // Build a map of flaw_id -> reasoning from analysis_data
  const reasoningMap = new Map<string, string>();
  if (analysis_data?.phase_summaries) {
    analysis_data.phase_summaries.forEach((phaseSummary: any) => {
      if (phaseSummary.observations) {
        phaseSummary.observations.forEach((obs: any) => {
          if (obs.flaw_id && obs.reasoning) {
            // If multiple observations for same flaw_id, combine reasoning
            const existing = reasoningMap.get(obs.flaw_id);
            reasoningMap.set(obs.flaw_id, existing 
              ? `${existing}\n\n${obs.reasoning}` 
              : obs.reasoning
            );
          }
        });
      }
    });
  }
  
  // Use coaching_result (new multi-agent pipeline)
  if (coaching_result) {
    console.log('[VideoAnalysis] Using coaching_result (coaching_v2)', {
      assessmentsCount: coaching_result.assessments?.length,
      drillsCount: coaching_result.recommendations?.drills?.length,
      hasSummary: !!coaching_result.summary,
      hasAnalysisData: !!analysis_data,
      analysisDataPhaseSummaries: analysis_data?.phase_summaries?.length || 0,
      hasReasoning: reasoningMap.size > 0,
      reasoningMapKeys: Array.from(reasoningMap.keys()),
      reasoningMapSize: reasoningMap.size,
      assessmentFlawIds: (coaching_result.assessments || []).map((a: any) => a.flaw_id)
    });
    
    // Primary Focus Track: assessments should only contain primary/secondary faults (max 2)
    const assessments = coaching_result.assessments || [];
    const primaryFault = assessments[0] || null;
    const secondaryFault = assessments.length > 1 ? assessments[1] : null;
    
    // Get all faults from analysis_data for "other observations"
    const allFaultsFromAnalysis: any[] = [];
    if (analysis_data?.phase_summaries) {
      analysis_data.phase_summaries.forEach((phaseSummary: any) => {
        if (phaseSummary.observations) {
          phaseSummary.observations.forEach((obs: any) => {
            if (obs.flaw_id) {
              allFaultsFromAnalysis.push(obs);
            }
          });
        }
      });
    }
    
    // Filter out primary and secondary from "other observations"
    const primaryFaultId = primaryFault?.flaw_id;
    const secondaryFaultId = secondaryFault?.flaw_id;
    const otherFaults = allFaultsFromAnalysis.filter(obs => 
      obs.flaw_id !== primaryFaultId && obs.flaw_id !== secondaryFaultId
    );
    
    return {
      // Primary and secondary faults (for coaching focus)
      flaws: assessments.map((assessment: any) => {
        const reasoning = reasoningMap.get(assessment.flaw_id) || null;
        console.log('[VideoAnalysis] Mapping flaw:', {
          flaw_id: assessment.flaw_id,
          hasReasoning: !!reasoning,
          reasoningLength: reasoning?.length || 0
        });
        return {
          id: assessment.flaw_id,
          flaw_id: assessment.flaw_id,
          display_name: assessment.display_name,
          short_description: assessment.description,
          severity: assessment.severity,
          confidence: assessment.confidence,
          player_language_symptoms: assessment.description,
          swing_thoughts: coaching_result.recommendations?.swing_thoughts || [],
          drill_ids: [],
          reasoning: reasoning, // Add reasoning from analysis_data
          is_primary: assessment.flaw_id === primaryFaultId,
          is_secondary: assessment.flaw_id === secondaryFaultId
        };
      }),
      // Other faults (for minimized display)
      other_faults: otherFaults.map((obs: any) => ({
        flaw_id: obs.flaw_id,
        display_name: obs.flaw_id?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || obs.flaw_id,
        severity: obs.severity
      })),
      drills: (coaching_result.recommendations?.drills || []).map((drill: any) => ({
        id: drill.drill_id,
        name: drill.title,
        why: drill.why,
        how: Array.isArray(drill.instructions) 
          ? drill.instructions.join('\n\n') 
          : drill.instructions || '',
        drill_ids: [drill.drill_id]
      })),
      swing_thoughts: coaching_result.recommendations?.swing_thoughts || [],
      coaching_bullets: [],
      coach_narrative: coaching_result.summary || '',
      summary: coaching_result.summary || '',
      confidence: coaching_result.confidence || 0.75,
      insufficient: false,
      // Primary Focus Track indicators
      primary_fault: primaryFault,
      secondary_fault: secondaryFault
    };
  }
  
  // Fallback to legacy ai_result (coaching_v1)
  if (ai_result) {
    console.log('[VideoAnalysis] Using legacy ai_result (coaching_v1)', {
      faultsCount: ai_result.faults?.length,
      drillsCount: ai_result.drills?.length
    });
    
    return {
      flaws: (ai_result.faults || []).map((fault: any, index: number) => ({
        id: fault.id || `fault_${index}`,
        flaw_id: fault.id || `fault_${index}`,
        display_name: fault.name || fault.id?.replace(/_/g, ' ') || `Fault ${index + 1}`,
        short_description: fault.evidence || fault.description || '',
        severity: fault.severity || 2,
        confidence: fault.confidence || 'medium',
        player_language_symptoms: fault.symptoms || fault.evidence || '',
        swing_thoughts: fault.swing_thoughts || [],
        drill_ids: fault.recommended_drills || []
      })),
      drills: (ai_result.drills || []).map((drill: any, index: number) => ({
        id: drill.id || `drill_${index}`,
        name: drill.title || drill.name || `Drill ${index + 1}`,
        why: drill.why || drill.rationale || '',
        how: Array.isArray(drill.steps) 
          ? drill.steps.join('\n\n') 
          : (drill.how || drill.instructions || ''),
        drill_ids: [drill.id || `drill_${index}`]
      })),
      swing_thoughts: [],
      coaching_bullets: [],
      coach_narrative: ai_result.summary || '',
      summary: ai_result.summary || '',
      confidence: ai_result.confidence || 0.75,
      insufficient: false
    };
  }
  
  return null;
}

export default function VideoAnalysis() {
  const { videoId } = useParams<{ videoId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Determine if this is a Cloudflare run or legacy video
  const isCloudflareRun = FEATURE_FLAGS.FEATURE_VIDEOS_CF && videoId && videoId.length > 30; // UUIDs are longer
  
  const [run, setRun] = useState<SwingRun | null>(null);
  const [video, setVideo] = useState<VideoData | null>(null);
  const [poseFrames, setPoseFrames] = useState<PoseFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  
  // Primary Focus Track: State for collapsible sections
  const [showSecondary, setShowSecondary] = useState(false);
  const [showOther, setShowOther] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerHostRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  // S-3057E: Memoize poseData to prevent OverlayCanvasV2 from unmounting/remounting
  // Inline object creation causes new reference on every render, breaking draw loop
  const memoizedPoseData = useMemo<PoseData | undefined>(() => {
    if (!run?.pose_data?.pose) return undefined;
    return {
      landmarks: run.pose_data.pose.landmarks,
      meta: {
        durationSec: run.pose_data.meta?.durationSec || 0,
        width: run.pose_data.meta?.width || 720,
        height: run.pose_data.meta?.height || 1280,
        fps: run.pose_data.meta?.fps || 30,
      }
    };
  }, [run?.pose_data]);

  // CRITICAL FIX: Extract pose frames from run data when available
  // ALSO normalize timestamps to start at 0.0 (S-3050A flickering fix)
  useEffect(() => {
    if (!run?.pose_data?.pose?.landmarks) return;
    
    const rawFrames = run.pose_data.pose.landmarks;
    
    // CRITICAL: Normalize timestamps so first frame = 0.0
    // Pose extraction may start partway through video (e.g. at 0.433s)
    // This causes massive flickering as skeleton shows future/past positions
    const firstTimestamp = rawFrames[0]?.timestamp || 0;
    const normalizedFrames = rawFrames.map((frame: PoseFrame) => ({
      ...frame,
      timestamp: frame.timestamp - firstTimestamp
    }));
    
    console.log('[VideoAnalysis] Extracting pose frames from run data:', {
      totalFrames: normalizedFrames.length,
      firstFrameRaw: rawFrames[0],
      firstFrameNormalized: normalizedFrames[0],
      timestampOffset: firstTimestamp,
      lastFrameNormalized: normalizedFrames[normalizedFrames.length - 1]
    });
    
    setPoseFrames(normalizedFrames);
  }, [run?.pose_data]);

  // S-3050A: Initialize HLS.js for Cloudflare playback
  useEffect(() => {
    if (!run?.cf_playback_url || !videoRef.current) return;

    const video = videoRef.current;
    const playbackUrl = run.cf_playback_url;

    // Validate URL - reject test URLs and invalid domains
    const isTestUrl = playbackUrl.includes('test.example.com') || 
                     playbackUrl.includes('example.com') ||
                     playbackUrl.includes('localhost') ||
                     playbackUrl.includes('127.0.0.1');
    
    const isValidCloudflareUrl = playbackUrl.includes('cloudflarestream.com') || 
                                  playbackUrl.includes('videodelivery.net');
    const isValidMuxUrl = playbackUrl.includes('stream.mux.com') || 
                           playbackUrl.includes('mux.com');
    
    if (isTestUrl || (!isValidCloudflareUrl && !isValidMuxUrl)) {
      console.error('[VideoAnalysis] Invalid or test playback URL detected:', {
        url: playbackUrl,
        isTestUrl,
        isValidCloudflareUrl,
        isValidMuxUrl
      });
      toast({
        title: "Invalid Video URL",
        description: "The video URL is invalid or contains test data. Please contact support.",
        variant: "destructive"
      });
      return;
    }

    console.log('[VideoAnalysis] Initializing HLS playback:', playbackUrl);

    // Cleanup previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Check for HLS support
    if (Hls.isSupported()) {
      // CRITICAL FIX (S-3050A): Force HLS to use the resolution matching pose extraction
      // Cloudflare serves multiple resolutions (720p, 1080p, etc) but pose was extracted from ONE specific resolution
      // We MUST force HLS to use that exact resolution or the overlay will be misaligned!
      const poseWidth = run.pose_data?.meta?.width || 720;
      const poseHeight = run.pose_data?.meta?.height || 1280;
      
      console.log('[VideoAnalysis] Configuring HLS for pose-matching resolution:', { poseWidth, poseHeight });
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        // FORCE specific quality level - do NOT auto-switch!
        capLevelToPlayerSize: false,  // Don't auto-adjust based on player size
        maxMaxBufferLength: 30,  // Shorter buffer to prevent quality switching
        autoLevelCapping: -1,  // Disable level capping
      });

      hls.loadSource(playbackUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log('[VideoAnalysis] HLS manifest parsed:', {
          levels: data.levels.map((l: any) => ({ width: l.width, height: l.height, bitrate: l.bitrate })),
          firstLevel: data.firstLevel
        });
        
        // Find the level that EXACTLY matches pose extraction dimensions
        const matchingLevelIndex = data.levels.findIndex((level: any) => 
          level.height === poseHeight && level.width === poseWidth
        );
        
        if (matchingLevelIndex !== -1) {
          console.log('[VideoAnalysis] Found matching resolution at level', matchingLevelIndex);
          // LOCK to this level by setting both currentLevel and nextLoadLevel
          hls.currentLevel = matchingLevelIndex;
          hls.nextLoadLevel = matchingLevelIndex;
        } else {
          console.warn('[VideoAnalysis] No exact resolution match found! Using closest:', {
            available: data.levels.map((l: any) => ({ w: l.width, h: l.height })),
            needed: { w: poseWidth, h: poseHeight }
          });
          // Fall back to lowest resolution (most likely to match)
          hls.currentLevel = 0;
          hls.nextLoadLevel = 0;
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[VideoAnalysis] HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('[VideoAnalysis] Fatal network error, trying to recover');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('[VideoAnalysis] Fatal media error, trying to recover');
              hls.recoverMediaError();
              break;
            default:
              console.error('[VideoAnalysis] Fatal error, cannot recover');
              hls.destroy();
              break;
          }
        }
      });

      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = playbackUrl;
      console.log('[VideoAnalysis] Using native HLS support');
    } else {
      console.error('[VideoAnalysis] HLS not supported in this browser');
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [run?.cf_playback_url]);

  useEffect(() => {
    if (!videoId) {
      navigate('/videos');
      return;
    }
    
    if (isCloudflareRun) {
      loadCloudflareRun();
    } else {
      loadLegacyVideo();
    }
  }, [videoId, isCloudflareRun]);

  const loadCloudflareRun = async () => {
    try {
      setLoading(true);
      
      const { data: runData, error: runError } = await supabase
        .from('swing_runs')
        .select('id, user_id, status, cf_stream_uid, cf_playback_url, original_filename, created_at, updated_at, pose_data, analysis_enriched, analysis_data, coaching_result, ai_result')
        .eq('id', videoId)
        .single();

      if (runError) {
        console.error('Error loading run:', runError);
        toast({
          title: "Error",
          description: "Failed to load video analysis",
          variant: "destructive"
        });
        navigate('/videos');
        return;
      }

      setRun(runData);

      // Debug: Log what columns we actually got from the database
      console.log('[VideoAnalysis] Run data loaded from DB:', {
        id: runData.id,
        status: runData.status,
        hasCoachingResult: !!runData.coaching_result,
        hasAnalysisData: !!runData.analysis_data,
        hasAnalysisEnriched: !!runData.analysis_enriched,
        hasAiResult: !!runData.ai_result,
        coachingResultType: typeof runData.coaching_result,
        analysisDataType: typeof runData.analysis_data,
        analysisEnrichedType: typeof runData.analysis_enriched,
        swingQuality: runData.analysis_enriched?.swing_quality,
        peerLabel: runData.analysis_enriched?.peer_label,
        allKeys: Object.keys(runData).filter(k => k.includes('coaching') || k.includes('analysis') || k.includes('ai_result') || k.includes('enriched'))
      });
      
      // CRITICAL: Also log the raw analysis_enriched to see if it's actually there
      console.log('[VideoAnalysis] Raw analysis_enriched check:', {
        exists: 'analysis_enriched' in runData,
        isNull: runData.analysis_enriched === null,
        isUndefined: runData.analysis_enriched === undefined,
        type: typeof runData.analysis_enriched,
        value: runData.analysis_enriched
      });
      
      // EXPAND the object to see all properties
      console.log('[VideoAnalysis] Full runData object keys:', Object.keys(runData));
      console.log('[VideoAnalysis] analysis_enriched value:', runData.analysis_enriched);
      console.log('[VideoAnalysis] analysis_enriched swing_quality:', runData.analysis_enriched?.swing_quality);
      console.log('[VideoAnalysis] analysis_enriched peer_label:', runData.analysis_enriched?.peer_label);
      
      // CRITICAL: If analysis_enriched is missing, log all columns to debug
      if (!runData.analysis_enriched) {
        console.error('[VideoAnalysis] ❌ analysis_enriched is MISSING from query result!');
        console.error('[VideoAnalysis] All columns:', Object.keys(runData));
        console.error('[VideoAnalysis] Full runData:', JSON.stringify(runData, null, 2));
      }

      // Extract pose frames if available
      console.log('[VideoAnalysis] Pose data structure:', {
        hasPoseData: !!runData.pose_data,
        poseDataKeys: runData.pose_data ? Object.keys(runData.pose_data) : [],
        hasPose: !!runData.pose_data?.pose,
        poseKeys: runData.pose_data?.pose ? Object.keys(runData.pose_data.pose) : [],
        hasLandmarks: !!runData.pose_data?.pose?.landmarks,
        landmarksType: typeof runData.pose_data?.pose?.landmarks,
        landmarksIsArray: Array.isArray(runData.pose_data?.pose?.landmarks),
        landmarksLength: runData.pose_data?.pose?.landmarks?.length
      });
      
      if (runData.pose_data?.pose?.landmarks && Array.isArray(runData.pose_data.pose.landmarks)) {
        const frames = runData.pose_data.pose.landmarks;
        
        // S-3050B: Read pose FPS from metadata (fallback to 30)
        const poseFps = runData.pose_data?.meta?.fps ?? 30;
        
        // S-3050A CRITICAL FIX: Normalize timestamps to start at 0.0
        // The pose data may start at a non-zero timestamp (e.g. 0.433s), but the video starts at 0.0
        // This offset causes severe flickering as the skeleton shows future positions
        const firstTimestamp = frames[0]?.timestamp || 0;
        const normalizedFrames = frames.map((f: any) => ({
          ...f,
          timestamp: f.timestamp - firstTimestamp
        }));
        
        console.log('[VideoAnalysis] Setting pose frames (NORMALIZED):', {
          totalFrames: normalizedFrames.length,
          poseFps, // S-3050B: Log captured FPS
          originalFirstTimestamp: firstTimestamp,
          firstFrame: normalizedFrames[0],
          sampleTimestamps: normalizedFrames.slice(0, 5).map((f: any) => f.timestamp)
        });
        setPoseFrames(normalizedFrames);
      } else {
        console.warn('[VideoAnalysis] No pose landmarks found or invalid format');
      }
    } catch (err) {
      console.error('Failed to load Cloudflare run:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLegacyVideo = async () => {
    try {
      setLoading(true);
      
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('*')
        .eq('id', videoId)
        .single();

      if (videoError) {
        console.error('Error loading video:', videoError);
        toast({
          title: "Error",
          description: "Failed to load video details",
          variant: "destructive"
        });
        navigate('/videos');
        return;
      }

      setVideo(videoData);
    } catch (err) {
      console.error('Failed to load legacy video:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVideoRef = (el: HTMLVideoElement | null) => {
    if (el && videoRef) {
      (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
    }
  };

  const getFrameAtTime = (time: number): any => {
    if (!poseFrames || poseFrames.length === 0) {
      console.warn('[VideoAnalysis] getFrameAtTime: no frames!', { time, framesLength: poseFrames?.length });
      return null;
    }
    
    // S-3050A FLICKERING FIX: Linear interpolation between frames
    // Pose data is ~30 FPS, video is ~60 FPS → need to interpolate
    
    // Find the two frames that bracket the current time
    let beforeFrame = poseFrames[0];
    let afterFrame = poseFrames[0];
    
    for (let i = 0; i < poseFrames.length - 1; i++) {
      if (poseFrames[i].timestamp <= time && poseFrames[i + 1].timestamp >= time) {
        beforeFrame = poseFrames[i];
        afterFrame = poseFrames[i + 1];
        break;
      }
    }
    
    // If we're past the last frame, use the last frame
    if (time >= poseFrames[poseFrames.length - 1].timestamp) {
      beforeFrame = poseFrames[poseFrames.length - 1];
      afterFrame = poseFrames[poseFrames.length - 1];
    }
    
    // Calculate interpolation factor (0.0 = beforeFrame, 1.0 = afterFrame)
    const timeDiff = afterFrame.timestamp - beforeFrame.timestamp;
    const alpha = timeDiff > 0 ? (time - beforeFrame.timestamp) / timeDiff : 0;
    
    // Interpolate landmarks
    const interpolatedLandmarks = beforeFrame.landmarks.map((beforeLm: any, idx: number) => {
      const afterLm = afterFrame.landmarks[idx];
      
      return {
        x: beforeLm.x + (afterLm.x - beforeLm.x) * alpha,
        y: beforeLm.y + (afterLm.y - beforeLm.y) * alpha,
        z: beforeLm.z + (afterLm.z - beforeLm.z) * alpha,
        visibility: beforeLm.visibility + (afterLm.visibility - beforeLm.visibility) * alpha
      };
    });
    
    // Use the interpolated frame as our "closest"
    const closest = {
      landmarks: interpolatedLandmarks,
      timestamp: time,
      frameIndex: beforeFrame.frameIndex
    };
    
    // EMERGENCY SCALING FIX (S-3050A):
    // Pose was extracted from 720x1280, but HLS serves 1080x1920
    // Need to detect and compensate for dimension mismatch
    const POSE_EXTRACTED_WIDTH = run?.pose_data?.meta?.width || 720;
    const POSE_EXTRACTED_HEIGHT = run?.pose_data?.meta?.height || 1280;
    const actualVideoWidth = videoRef.current?.videoWidth || POSE_EXTRACTED_WIDTH;
    const actualVideoHeight = videoRef.current?.videoHeight || POSE_EXTRACTED_HEIGHT;
    
    const scaleX = actualVideoWidth / POSE_EXTRACTED_WIDTH;
    const scaleY = actualVideoHeight / POSE_EXTRACTED_HEIGHT;
    
    const needsScaling = scaleX !== 1 || scaleY !== 1;
    
    // Convert DB format {x, y, z, visibility} to tuple format [x, y, z, visibility]
    // AND apply scaling correction if dimensions don't match
    const landmarkTuples = closest.landmarks.map((lm: any) => {
      // MediaPipe landmarks are normalized (0-1), but our dimension mismatch
      // means we need to pass through the actual video dimensions for proper scaling
      // The OverlayCanvas will handle the normalization → pixels conversion
      return [lm.x, lm.y, lm.z, lm.visibility] as [number, number, number, number];
    });
    
    const wrappedFrame = {
      pose: {
        landmarks: [landmarkTuples]  // Wrap tuple array in array as OverlayCanvas expects landmarks[0]
      },
      timestamp: closest.timestamp,
      frameIndex: closest.frameIndex,
      // CRITICAL: Pass dimension metadata to OverlayCanvas
      _dimensionCorrection: needsScaling ? {
        poseWidth: POSE_EXTRACTED_WIDTH,
        poseHeight: POSE_EXTRACTED_HEIGHT,
        videoWidth: actualVideoWidth,
        videoHeight: actualVideoHeight,
        scaleX,
        scaleY
      } : undefined
    };
    
    if (frameCount === 0 || frameCount % 120 === 0) {
      console.log('[VideoAnalysis] getFrameAtTime INTERPOLATED:', { 
        time, 
        beforeTimestamp: beforeFrame.timestamp,
        afterTimestamp: afterFrame.timestamp,
        alpha: alpha.toFixed(3),
        interpolated: beforeFrame.timestamp !== afterFrame.timestamp,
        poseExtractedDims: { w: POSE_EXTRACTED_WIDTH, h: POSE_EXTRACTED_HEIGHT },
        actualVideoDims: { w: actualVideoWidth, h: actualVideoHeight },
        scale: { x: scaleX, y: scaleY },
        needsScaling,
        landmarksCount: landmarkTuples.length
      });
    }
    
    frameCount++;
    
    return wrappedFrame;
  };
  
  let frameCount = 0; // For logging throttle

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analysis...</p>
        </div>
        <BottomTabs />
      </div>
    );
  }

  if (!run && !video) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="px-6 pt-8">
          <GolfButton
            variant="ghost"
            onClick={() => navigate('/videos')}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Videos
          </GolfButton>
          
          <GolfCard>
            <GolfCardContent className="p-6 text-center">
              <p className="text-muted-foreground">Video not found</p>
            </GolfCardContent>
          </GolfCard>
        </div>
        <BottomTabs />
      </div>
    );
  }

    // Render Cloudflare run
  if (run) {
    // Extract runId for UI components (scoring helpers) - does not affect pipeline
    const runId = run.id;
    const playbackUrl = run.cf_playback_url;
    const hasCoaching = !!run.coaching_result;
    const hasLegacyAnalysis = !!run.ai_result;
    const hasAnalysis = hasCoaching || hasLegacyAnalysis;
    const hasPose = !!run.pose_data;
    const analysis = hasAnalysis ? mapCoachingToUI(run.coaching_result, run.ai_result, run.analysis_data) : null;
    
    // Debug: Log what we're passing to mapCoachingToUI
    console.log('[VideoAnalysis] Data for mapping:', {
      hasCoaching: hasCoaching,
      hasLegacyAnalysis: hasLegacyAnalysis,
      hasAnalysisData: !!run.analysis_data,
      analysisDataPhaseSummaries: run.analysis_data?.phase_summaries?.length || 0,
      coachingAssessments: run.coaching_result?.assessments?.length || 0
    });
    
    // Extract key data from analysis (Primary Focus Track)
    const primaryFault = analysis?.primary_fault || analysis?.flaws?.[0];
    const secondaryFault = analysis?.secondary_fault || (analysis?.flaws?.length > 1 ? analysis?.flaws?.[1] : null);
    const recommendedDrill = analysis?.drills?.[0];
    const otherFaults = analysis?.other_faults || [];
    
    // Debug logging
    console.log('[VideoAnalysis] Run loaded:', {
      runId: run.id,
      status: run.status,
      hasCoaching: hasCoaching,
      hasLegacyAnalysis: hasLegacyAnalysis,
      hasAnalysis,
      hasPose,
      poseFramesCount: poseFrames.length,
      landmarksInData: run.pose_data?.pose?.landmarks?.length || 0,
      primaryFault: primaryFault?.flaw_id,
      secondaryFault: secondaryFault?.flaw_id,
      otherFaultsCount: otherFaults.length
    });
    
    // Legacy: Extract other flaws (excluding key flaw, top 3) - for backward compatibility
    const otherFlaws = analysis?.flaws?.slice(1, 4) || [];
    
    // Extract swing quality metrics from analysis_enriched
    // Try multiple access patterns in case of type issues
    const analysisEnriched = (run as any).analysis_enriched || run.analysis_enriched;
    const swingQuality = analysisEnriched?.swing_quality ?? (run as any).analysis_enriched?.swing_quality;
    const peerLabel = analysisEnriched?.peer_label ?? (run as any).analysis_enriched?.peer_label;
    const enrichedObservations = analysisEnriched?.phase_summaries?.flatMap((ps: any) => ps.observations || []) || [];
    
    // Debug: Log swing quality data - FORCE EXPANSION
    console.log('[VideoAnalysis] Swing Quality Debug:', {
      hasAnalysisEnriched: !!analysisEnriched,
      swingQuality,
      peerLabel,
      enrichedObservationsCount: enrichedObservations.length,
      analysisEnrichedKeys: analysisEnriched ? Object.keys(analysisEnriched) : [],
      runKeys: Object.keys(run).filter(k => k.includes('analysis') || k.includes('enriched')),
      rawAnalysisEnriched: analysisEnriched
    });
    
    // SEPARATE LOGS to force visibility
    console.log('[VideoAnalysis] swingQuality value:', swingQuality);
    console.log('[VideoAnalysis] peerLabel value:', peerLabel);
    console.log('[VideoAnalysis] Will show card?', swingQuality !== undefined && swingQuality !== null);
    
    // Helper to format peer label
    const formatPeerLabel = (label?: string) => {
      if (!label) return null;
      const labels: Record<string, string> = {
        'weak_for_level': 'Needs Work',
        'solid_for_level': 'Solid',
        'strong_for_level': 'Strong',
        'elite_for_level': 'Elite'
      };
      return labels[label] || label;
    };
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-[#F5F5F5] pb-20">
        {/* MVPFIGMA Header */}
        <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-6 py-4 flex items-center justify-between h-20 shadow-sm">
          <div className="flex-1 flex justify-center">
            <LogoOption4 color="#FFFFFF" className="h-12" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/videos')}
            className="text-white/90 hover:text-white hover:bg-white/10 text-sm"
          >
            View All
          </Button>
        </header>
        
        <main className="max-w-4xl mx-auto">

          {/* MVPFIGMA: Video Player Section */}
          <div className="px-6 pt-8 pb-6">
            <div className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(13,56,42,0.12)] overflow-hidden relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] opacity-100 -z-10" style={{ padding: '2px' }}>
                <div className="absolute inset-[2px] bg-white rounded-2xl" />
              </div>
              
              <div className="relative z-10 rounded-xl overflow-hidden m-[2px]">
                {/* S-3050A: Relative container for video + overlay canvas */}
                <div className="relative w-full aspect-video bg-black overflow-hidden" ref={playerHostRef}>
                  {playbackUrl ? (
                    <>
                      {/* S-3050A: HLS.js playback for Cloudflare videos */}
                      <video
                        ref={handleVideoRef}
                        controls
                        playsInline
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{ zIndex: 1 }}
                        onPlay={() => setIsVideoPlaying(true)}
                        onPause={() => setIsVideoPlaying(false)}
                        onLoadedMetadata={() => {
                          console.log('[VideoAnalysis] Video metadata loaded, dimensions:', {
                            videoWidth: videoRef.current?.videoWidth,
                            videoHeight: videoRef.current?.videoHeight,
                            clientWidth: videoRef.current?.clientWidth,
                            clientHeight: videoRef.current?.clientHeight
                          });
                        }}
                      >
                        Your browser does not support HLS playback.
                      </video>
                      
                      {hasPose && showOverlay && (
                        <div className="absolute inset-0" style={{ zIndex: 2, pointerEvents: 'none' }}>
                          <OverlayController
                            videoRef={videoRef}
                            getLatestFrame={() => null}
                            getFrameAtTime={getFrameAtTime}
                            isLiveCapture={false}
                            visible={true}
                            opacity={80}
                            showSkeleton={true}
                            handsOnly={false}
                            poseData={memoizedPoseData}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="text-muted-foreground">No playback URL available</p>
                    </div>
                  )}
                </div>
                
                {/* Pose Overlay Toggle Footer */}
                {hasPose && (
                  <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#0D382A]">Pose Overlay</p>
                      <p className="text-xs text-gray-500">
                        {poseFrames.length} frames detected
                      </p>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowOverlay(!showOverlay)}
                      className="border-[#196644] text-[#196644] hover:bg-[#196644] hover:text-white h-8"
                    >
                      {showOverlay ? (
                        <>
                          <EyeOff className="mr-2 h-4 w-4" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="mr-2 h-4 w-4" />
                          Show
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Swing Quality Score Card */}
          {(() => {
            // Force check - log what we have
            const hasQuality = swingQuality !== undefined && swingQuality !== null;
            console.log('[VideoAnalysis] Rendering check - swingQuality:', swingQuality, 'willShow:', hasQuality);
            return hasQuality;
          })() && (
            <div className="px-6 pb-6">
              <div className="bg-gradient-to-r from-[#0D382A] to-[#196644] rounded-2xl p-6 shadow-[0_4px_16px_rgba(13,56,42,0.12)] text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-semibold">Swing Quality Score</h3>
                      {runId && swingQuality !== undefined && swingQuality !== null && (
                        <ScoreInfoIcon 
                          score={swingQuality} 
                          runId={runId}
                          size="sm"
                        />
                      )}
                    </div>
                    <p className="text-white/80 text-sm">
                      {peerLabel ? `Compared to ${formatPeerLabel(peerLabel)} players at your level` : 'Overall swing quality'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold">{swingQuality.toFixed(1)}</div>
                    <div className="text-sm text-white/80">out of 100</div>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full h-4 bg-white/20 rounded-full overflow-hidden mb-2">
                  <div 
                    className="h-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] rounded-full transition-all duration-500"
                    style={{ width: `${swingQuality}%` }}
                  />
                </div>
                
                {peerLabel && (
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                        {formatPeerLabel(peerLabel)}
                      </Badge>
                      <span className="text-sm text-white/80">
                        for your skill level
                      </span>
                    </div>
                    {runId && swingQuality !== undefined && swingQuality !== null && (
                      <GolfButton
                        variant="outline"
                        size="sm"
                        className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                        onClick={() => navigate(`/score-explanation/swing/${runId}`)}
                      >
                        View Breakdown
                      </GolfButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Primary Focus Track: Primary Focus Card */}
          {hasAnalysis && primaryFault && (
            <div className="px-6 pb-6">
              <div className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] rounded-2xl p-4 sm:p-6 shadow-[0_4px_16px_rgba(13,56,42,0.12)] border-2 border-[#196644]">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-start sm:gap-4">
                  {/* Icon */}
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-[#0D382A] flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  
                  {/* Text Block */}
                  <div className="w-full text-left">
                    {/* Title + Badge Wrapper */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 mb-2">
                      <h3 className="text-[#0D382A] text-lg sm:text-xl font-semibold">Primary Focus</h3>
                      <Badge className="bg-[#196644] text-white w-fit">High Impact</Badge>
                    </div>
                    
                    {/* Main Title (Fault Name) */}
                    <p className="text-lg sm:text-2xl text-[#0D382A] font-bold mb-2">
                      {primaryFault.display_name || primaryFault.name}
                    </p>
                    
                    {/* Subtitle (Description) */}
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                      {primaryFault.short_description || primaryFault.player_language_symptoms}
                    </p>
                    
                    {/* Note */}
                    <p className="text-sm text-gray-600 italic">
                      We identified multiple biomechanical patterns, but the fastest way for you to improve is to focus on this primary area.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Primary Focus Track: Secondary Focus (Collapsible) */}
          {hasAnalysis && secondaryFault && (
              <div className="px-6 pb-6">
                <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] border border-gray-200">
                  <button
                    onClick={() => setShowSecondary(!showSecondary)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-orange-500" />
                      <h3 className="text-[#0D382A] text-lg font-semibold">Secondary Issue (Optional for later)</h3>
                    </div>
                    {showSecondary ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  {showSecondary && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-lg text-[#196644] font-medium mb-2">
                        {secondaryFault.display_name || secondaryFault.name}
                      </p>
                      <p className="text-gray-600 leading-relaxed">
                        {secondaryFault.short_description || secondaryFault.player_language_symptoms}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Primary Focus Track: Other Observations (Minimized) */}
          {hasAnalysis && otherFaults.length > 0 && (
              <div className="px-6 pb-6">
                <div className="bg-gray-50 rounded-2xl p-4 shadow-[0_1px_4px_rgba(13,56,42,0.06)] border border-gray-200">
                  <button
                    onClick={() => setShowOther(!showOther)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-gray-400" />
                      <h3 className="text-gray-600 text-sm font-medium">Other biomechanical observations (for coaches only)</h3>
                    </div>
                    {showOther ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {showOther && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="space-y-2">
                        {otherFaults.map((fault: any, index: number) => (
                          <div key={index} className="text-sm text-gray-500">
                            <span className="font-medium">{fault.display_name || fault.flaw_id}</span>
                            {fault.severity !== undefined && (
                              <span className="ml-2 text-xs">(Severity: {fault.severity})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* MVPFIGMA: Scoring Impact Card */}
          {hasAnalysis && primaryFault && (
            <div className="px-6 pb-6">
              <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)] transition-shadow duration-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#A7F3A1] flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-[#0D382A]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#0D382A] text-xl font-semibold mb-3">Estimated Scoring Impact</h3>
                    <p className="text-gray-700 mb-3">
                      {primaryFault.severity >= 4 ? '3-4' : primaryFault.severity >= 3 ? '2-3' : '1-2'} strokes per round
                    </p>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          primaryFault.severity >= 4 ? 'w-full' : primaryFault.severity >= 3 ? 'w-3/4' : 'w-1/2'
                        } bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1]`}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#196644]">
                        {primaryFault.severity >= 4 ? 'High' : primaryFault.severity >= 3 ? 'Medium' : 'Low'} Impact
                      </span>
                      {/* Show impact_potential from enriched analysis if available */}
                      {(() => {
                        const enrichedFlaw = enrichedObservations.find((obs: any) => obs.flaw_id === primaryFault.flaw_id);
                        if (enrichedFlaw?.impact_potential) {
                          return (
                            <Badge variant="outline" className="border-[#196644] text-[#196644]">
                              {enrichedFlaw.impact_potential.charAt(0).toUpperCase() + enrichedFlaw.impact_potential.slice(1)} Potential
                            </Badge>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MVPFIGMA: What to Work On Section (Legacy - only show if no primary focus) */}
          {hasAnalysis && otherFlaws.length > 0 && !primaryFault && (
            <div className="px-6 pb-6">
              <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
                <div className="flex items-center gap-2 mb-4">
                  <ListChecks className="w-5 h-5 text-[#0D382A]" />
                  <h3 className="text-[#0D382A] text-xl font-semibold">What to Work On</h3>
                </div>
                <div className="space-y-3">
                  {otherFlaws.map((flaw: any, index: number) => {
                    const enrichedFlaw = enrichedObservations.find((obs: any) => obs.flaw_id === flaw.flaw_id);
                    return (
                      <div key={index} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                        <Target className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-[#0D382A]">
                              {flaw.display_name || flaw.name}
                            </p>
                            {enrichedFlaw?.impact_potential && (
                              <Badge variant="outline" className="text-xs">
                                {enrichedFlaw.impact_potential.charAt(0).toUpperCase() + enrichedFlaw.impact_potential.slice(1)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {flaw.short_description || flaw.player_language_symptoms}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* MVPFIGMA: Recommended Drill Card */}
          {hasAnalysis && recommendedDrill && (
            <div className="px-6 pb-6">
              <div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(13,56,42,0.12)] border-2 border-transparent relative overflow-hidden">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] opacity-30 -z-10" style={{ padding: '2px' }}>
                  <div className="absolute inset-[2px] bg-white rounded-2xl" />
                </div>

                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#196644] to-[#10B981] flex items-center justify-center">
                      <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#196644] mb-1">Recommended Drill</p>
                      <h3 className="text-[#0D382A] text-xl font-semibold">{recommendedDrill.name || recommendedDrill.title}</h3>
                      <p className="text-sm text-gray-500">5 minutes</p>
                    </div>
                  </div>
                  
                  {recommendedDrill.why && (
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2">
                      {recommendedDrill.why}
                    </p>
                  )}
                  
                  <button
                    onClick={() => navigate(`/swing-lab/drills?drill=${recommendedDrill.id || recommendedDrill.drill_ids?.[0]}`)}
                    className="w-full h-14 bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] hover:shadow-xl text-[#0D382A] transition-all duration-200 hover:scale-[1.02] rounded-full flex items-center justify-center"
                  >
                    <Play className="w-6 h-6 mr-2" />
                    <span className="text-lg font-semibold">Start This Drill</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MVPFIGMA: Analysis Summary Section (Expandable) */}
          {hasAnalysis && analysis && (
            <div className="px-6 pb-6">
              {!analysisExpanded ? (
                <div className="text-center">
                  <button
                    onClick={() => setAnalysisExpanded(true)}
                    className="w-full py-3 text-[#196644] font-medium hover:text-[#0D382A] transition-colors"
                  >
                    View Full Analysis
                    <ChevronDown className="w-5 h-5 inline ml-1" />
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] space-y-4 animate-in fade-in duration-200">
                  {/* Complete Analysis Subsection */}
                  {analysis.summary && (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-5 h-5 text-[#196644]" />
                        <h3 className="text-[#0D382A] text-lg font-semibold">Complete Analysis</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{analysis.summary}</p>
                    </>
                  )}

                  {/* All Opportunities Subsection */}
                  {analysis.flaws && analysis.flaws.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 mt-6 mb-3">
                        <Target className="w-5 h-5 text-orange-500" />
                        <h3 className="text-[#0D382A] text-lg font-semibold">All Opportunities</h3>
                      </div>
                      <div className="space-y-3">
                        {analysis.flaws.map((flaw: any, index: number) => (
                          <FlawCardWithReasoning key={index} flaw={flaw} />
                        ))}
                      </div>
                    </>
                  )}

                  {/* Collapse Button */}
                  <button
                    onClick={() => setAnalysisExpanded(false)}
                    className="w-full text-center py-3 text-[#196644] font-medium hover:text-[#0D382A] transition-colors mt-4"
                  >
                    Collapse Analysis
                    <ChevronUp className="w-5 h-5 inline ml-1" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* MVPFIGMA: Quick Stats Section (Optional) */}
          {hasPose && run.pose_data?.meta && (
            <div className="px-6 pb-8">
              <div className="bg-white rounded-2xl p-5 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
                <div className="flex items-center gap-2 mb-4">
                  <Info className="w-5 h-5 text-gray-400" />
                  <h3 className="text-[#0D382A] text-lg font-semibold">Video Info</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Frames Analyzed</p>
                    <p className="text-lg font-semibold text-[#0D382A]">{poseFrames.length} frames</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Duration</p>
                    <p className="text-lg font-semibold text-[#0D382A]">{run.pose_data.meta.durationSec?.toFixed(1) || 'N/A'} seconds</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MVPFIGMA: Processing State */}
          {!hasAnalysis && run.status === 'processing' && (
            <div className="px-6 pb-6">
              <ProcessingNarrative videoPreviewUrl={playbackUrl} />
            </div>
          )}

          {/* MVPFIGMA: Failed State */}
          {!hasAnalysis && run.status === 'failed' && (
            <div className="px-6 pb-6">
              <div className="bg-white rounded-2xl p-8 shadow-[0_2px_8px_rgba(13,56,42,0.08)] text-center">
                <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#0D382A] mb-2">Analysis Failed</h3>
                <p className="text-gray-600 mb-4">
                  We couldn't complete the analysis. Please try uploading again.
                </p>
                <Button
                  onClick={() => navigate('/swing-lab/upload')}
                  className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:shadow-lg"
                >
                  Upload New Swing
                </Button>
              </div>
            </div>
          )}
        </main>
        <BottomTabs />
      </div>
    );
  }

  // Render legacy video (fallback)
  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="px-6 pt-8">
        <GolfButton
          variant="ghost"
          onClick={() => navigate('/videos')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Videos
        </GolfButton>
        
        <GolfCard>
          <GolfCardHeader>
            <GolfCardTitle>Legacy Video</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <p className="text-muted-foreground mb-4">
              This is a legacy Mux video. Full analysis is not yet available for legacy videos.
            </p>
            {video && (
              <div className="space-y-2 text-sm">
                <p><strong>ID:</strong> {video.id}</p>
                <p><strong>Status:</strong> {video.status}</p>
                <p><strong>Created:</strong> {new Date(video.created_at).toLocaleString()}</p>
              </div>
            )}
          </GolfCardContent>
        </GolfCard>
      </div>
      <BottomTabs />
    </div>
  );
}
